import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection, MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromDraftDiscount, toDiscountBody } from '../api/mappers';
import { useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import {
  discountSchema,
  type DiscountFormInput,
  type DiscountFormOutput,
} from '../schemas/discount-schema';
import { isStateConflict } from '../schemas/draft-order-rules';

const TYPES = ['none', 'fixed', 'percent'] as const;

export interface DraftDiscountCardProps {
  storeId: string;
  draft: DraftOrder;
  editable: boolean;
}

/** A staff discount on the whole draft; the backend caps a fixed amount at the subtotal. */
export function DraftDiscountCard({ storeId, draft, editable }: DraftDiscountCardProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const update = useUpdateDraftOrder(storeId, 'draftOrders:discount.saved');
  const form = useForm<DiscountFormInput, unknown, DiscountFormOutput>({
    resolver: useI18nResolver(discountSchema),
    defaultValues: fromDraftDiscount(draft),
  });
  const type = form.watch('type');

  /** `discount.value` is the amount or the percent, whichever the type shows. */
  const mapField = (field: string) => {
    const rest = field.replace(/^discount\./, '');
    if (rest === 'value') return form.getValues('type') === 'fixed' ? 'amount' : 'percent';
    return rest;
  };

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ id: draft.id, body: toDiscountBody(values) });
      form.reset(values);
    } catch (error) {
      const known = new Set(['amount', 'percent', 'reason']);
      if (applyFieldErrors(error, form.setError, { knownFields: known, mapField })) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  const applied = draft.discount ? (
    <p className="text-sm" data-testid="discount-applied">
      {t('draftOrders:discount.applied', { amount: format.money(draft.discountTotal) })}
      {draft.discount.reason ? ` · ${draft.discount.reason}` : null}
    </p>
  ) : null;

  if (!editable) {
    return (
      <FormSection title={t('draftOrders:discount.title')} tour="draft-orders.detail.discount">
        {applied ?? (
          <p className="text-muted-foreground text-sm">{t('draftOrders:discount.none')}</p>
        )}
      </FormSection>
    );
  }

  return (
    <FormSection title={t('draftOrders:discount.title')} tour="draft-orders.detail.discount">
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('draftOrders:discount.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('draftOrders:discount.type')}</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      form.clearErrors();
                    }}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {TYPES.map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`draftOrders:discount.${option}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {type === 'fixed' ? (
              <FormField
                control={form.control}
                name="amount"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('draftOrders:discount.amount')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={draft.currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {type === 'percent' ? (
              <FormField
                control={form.control}
                name="percent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('draftOrders:discount.percentValue')}</FormLabel>
                    <FormControl>
                      <Input inputMode="decimal" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
          </div>
          {type !== 'none' ? (
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('draftOrders:discount.reason')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder={t('draftOrders:discount.reasonPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          {applied}
          <div>
            <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending ? t('common:actions.saving') : t('draftOrders:discount.save')}
            </Button>
          </div>
        </form>
      </Form>
    </FormSection>
  );
}
